import React, { useState, useEffect } from 'react';
import { categorizeInventoryItems, testClaudeConnection, INVENTORY_CATEGORIES, DEFAULT_LOCATIONS } from '../config/openai';
import supabase from '../config/supabase';

const AITestingTool = ({ onClose }) => {
  const [testData, setTestData] = useState('');
  const [userLocations, setUserLocations] = useState(DEFAULT_LOCATIONS);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Sample test data
  const sampleData = `Whole Milk - 1 Gallon, Organic Valley, Sysco
Ground Beef - 80/20, , US Foods
Romaine Lettuce, , Local Farm
Coca Cola - 12 pack, Coca Cola, Sysco
Dish Soap - Industrial, Dawn, Restaurant Depot
Coffee Beans - Dark Roast, Starbucks, Sysco
Salmon Fillet - Fresh, , Seafood Direct
Paper Towels - 12 roll, Bounty, Restaurant Depot
Red Wine - Cabernet, Kendall Jackson, Wine Distributor
Olive Oil - Extra Virgin, Bertolli, US Foods`;

  useEffect(() => {
    setTestData(sampleData);
    loadUserLocations();
  }, []);

  const loadUserLocations = async () => {
    try {
      const { data: storeDetails } = await supabase
        .from('store_details')
        .select('inventory_locations')
        .eq('account', 'default-user')
        .single();

      if (storeDetails?.inventory_locations) {
        setUserLocations(storeDetails.inventory_locations);
      }
    } catch (error) {
      console.log('Using default locations:', error);
    }
  };

  const parseTestData = (data) => {
    return data.split('\n')
      .filter(line => line.trim())
      .map(line => {
        const parts = line.split(',').map(p => p.trim());
        return {
          description: parts[0] || '',
          brand: parts[1] || '',
          vendor: parts[2] || ''
        };
      });
  };

  const runTest = async () => {
    setLoading(true);
    setError(null);
    setResults(null);

    try {
      const items = parseTestData(testData);
      console.log('Testing with items:', items);
      console.log('Using locations:', userLocations);

      const categorizedItems = await categorizeInventoryItems(items, userLocations);
      setResults(categorizedItems);

    } catch (err) {
      setError(err.message);
      console.error('AI Test Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const testConnection = async () => {
    setLoading(true);
    setError(null);

    try {
              await testClaudeConnection();
              alert('Claude API connection successful!');
    } catch (err) {
      setError(`Connection failed: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ai-testing-tool">
      <div className="card">
        <div className="card-header">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">
              <i className="feather icon-cpu me-2"></i>
              AI Categorization Testing Tool
            </h5>
            <button className="btn btn-outline-secondary btn-sm" onClick={onClose}>
              <i className="feather icon-x"></i>
            </button>
          </div>
        </div>

        <div className="card-body">
          {/* API Status */}
          <div className="alert alert-info">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <strong>OpenAI API Status:</strong> Ready for testing
                <br />
                <small>Model: GPT-4 | Categories: {INVENTORY_CATEGORIES.length} | Locations: {userLocations.length}</small>
              </div>
              <button 
                className="btn btn-outline-primary btn-sm"
                onClick={testConnection}
                disabled={loading}
              >
                Test Connection
              </button>
            </div>
          </div>

          {/* Test Data Input */}
          <div className="mb-4">
            <label className="form-label">
              <strong>Test Data (CSV Format: Description, Brand, Vendor)</strong>
            </label>
            <textarea
              className="form-control"
              rows="10"
              value={testData}
              onChange={(e) => setTestData(e.target.value)}
              placeholder="Enter items in CSV format: Description, Brand, Vendor"
            />
            <small className="text-muted">
              One item per line. Format: "Item Description, Brand Name, Vendor Name"
              <br />
              Leave brand or vendor empty if unknown.
            </small>
          </div>

          {/* Available Categories */}
          <div className="row mb-4">
            <div className="col-md-6">
              <h6>Available Categories ({INVENTORY_CATEGORIES.length})</h6>
              <div className="border p-2" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                {INVENTORY_CATEGORIES.map((category, index) => (
                  <span key={index} className="badge bg-light text-dark me-1 mb-1">
                    {category}
                  </span>
                ))}
              </div>
            </div>
            <div className="col-md-6">
              <h6>Available Locations ({userLocations.length})</h6>
              <div className="border p-2" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                {userLocations.map((location, index) => (
                  <span key={index} className="badge bg-light text-dark me-1 mb-1">
                    {location}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Test Button */}
          <div className="text-center mb-4">
            <button
              className="btn btn-primary btn-lg"
              onClick={runTest}
              disabled={loading || !testData.trim()}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Processing with AI...
                </>
              ) : (
                <>
                  <i className="feather icon-cpu me-2"></i>
                  Run AI Categorization Test
                </>
              )}
            </button>
          </div>

          {/* Error Display */}
          {error && (
            <div className="alert alert-danger">
              <strong>Error:</strong> {error}
            </div>
          )}

          {/* Results Display */}
          {results && (
            <div className="mt-4">
              <h6>AI Categorization Results</h6>
              <div className="table-responsive">
                <table className="table table-bordered">
                  <thead className="table-light">
                    <tr>
                      <th>Description</th>
                      <th>Brand</th>
                      <th>Vendor</th>
                      <th>AI Category</th>
                      <th>AI Location</th>
                      <th>Reasoning</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.map((item, index) => (
                      <tr key={index}>
                        <td><strong>{item.description}</strong></td>
                        <td>{item.brand || '-'}</td>
                        <td>{item.vendor || '-'}</td>
                        <td>
                          <span className="badge bg-success">
                            {item.ai_suggested_category || 'Not assigned'}
                          </span>
                        </td>
                        <td>
                          <span className="badge bg-info">
                            {item.ai_suggested_location || 'Not assigned'}
                          </span>
                        </td>
                        <td>
                          <small className="text-muted">
                            {item.ai_reasoning || 'No reasoning provided'}
                          </small>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Summary */}
              <div className="alert alert-success mt-3">
                <strong>Test Summary:</strong>
                <ul className="mb-0 mt-2">
                  <li>Items processed: {results.length}</li>
                  <li>Categories assigned: {results.filter(r => r.ai_suggested_category).length}</li>
                  <li>Locations assigned: {results.filter(r => r.ai_suggested_location).length}</li>
                  <li>Errors: {results.filter(r => r.ai_error).length}</li>
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AITestingTool;
